import {useEffect, useRef} from 'react';
import {
  areMoreLazyLoadingEntriesNeeded,
  DEFAULT_NUM_ROWS,
  getEmailCampaignEntriesByViewType,
  getEmptyEntriesObject,
  getEntriesMapKey,
  getFilteredInternalEventsByViewType,
  getFilteredScheduledEntriesByViewType,
  getSocialPostEntriesByViewType,
  getUserEventEntriesByViewType,
  showEventsIntroPopupIfNeeded,
} from '@Libraries/content-planner-library';
import {
  setCustomEventFormDescription,
  setCustomEventFormTimestamp,
  setCustomEventFormTitle,
  updateEventRegionFilters,
  updateEventReligionFilters,
} from '@Components/content-planner/content-planner-reducer';
import {getEntriesForContentPlanner} from '@Components/content-planner/content-planner-thunk';
import {areDatesEqual, getDateFromUnixTimestamp, getDayMonthYearFormat, getMonthlyCalendarEndDate, getMonthlyCalendarStartDate, getUnixTimestamp} from '@Utils/date.util';
import type {
  BaseEvent,
  ContentPlannerEntries,
  ContentPlannerFilters,
  ContentPlannerState,
  ContentPlannerThunkParams,
  CustomEventFormSetters,
  EntriesMap,
  EntryTypeFilter,
  EventAreaFiltersFromStore,
  SocialGlobalEventFilter,
  CalendarEntriesViewType,
  FilteredContentPlannerEntries,
  ContentOwnershipTypeFilter,
} from '@Components/content-planner/content-planner.types';
import {EventType} from '@Components/content-planner/content-planner.types';
import type {FiltersHashmap} from '@Components/checkbox-filters/checkbox-filters.types';
import {noop} from '@Utils/general.util';
import {arraysEqual} from '@Utils/array.util';
import {useEventsFilters} from '@Components/events/events.hooks';
import {useAppDispatch, useAppSelector} from '@/hooks';

export const useContentPlannerDate = (): Date => {
  return useAppSelector((state) => {
    return state.contentPlanner.date;
  }, areContentPlannerDatesTheSame);
};

export const useContentOwnershipFilter = (): ContentOwnershipTypeFilter => {
  return useAppSelector((state) => {
    return state.contentPlanner.contentPlannerFilters.contentOwnerFilters;
  });
};

export const useEntriesMapKey = (): string => {
  const contentPlannerDate = useContentPlannerDate();
  return getEntriesMapKey(contentPlannerDate);
};

export const useContentPlannerActiveDate = (): Date => {
  return useAppSelector((state) => {
    return state.contentPlanner.activeDate;
  }, areContentPlannerDatesTheSame);
};

export const useIsActiveContentPlannerDate = (date: Date): boolean => {
  return useAppSelector((state) => {
    return areDatesEqual(state.contentPlanner.activeDate, date);
  });
};

export const useContentPlannerFilters = (): ContentPlannerFilters => {
  return useAppSelector((state) => {
    return state.contentPlanner.contentPlannerFilters;
  }, areFiltersTheSame);
};

export const useContentPlannerEntryFilters = (): EntryTypeFilter => {
  return useAppSelector((state) => {
    return state.contentPlanner.contentPlannerFilters.entryTypeFilters;
  });
};

export const useSocialGlobalEventFilters = (): SocialGlobalEventFilter => {
  return useAppSelector((state) => {
    return state.contentPlanner.contentPlannerFilters.socialGlobalEventFilters;
  });
};

export const useEventRegionFilters = (): EventAreaFiltersFromStore => {
  const filters = useAppSelector((state) => {
    return state.contentPlanner.contentPlannerFilters.eventRegionFilters;
  });
  const filtersSource = useAppSelector((state) => {
    return state.contentPlanner.regions;
  });

  const dispatch = useAppDispatch();
  const onChange = (newFiltersState: FiltersHashmap): void => {
    dispatch(updateEventRegionFilters(newFiltersState));
  };

  return {
    filters,
    filtersStateSetter: onChange,
    filtersSource,
  };
};

export const useEventReligionFilters = (): EventAreaFiltersFromStore => {
  const filters = useAppSelector((state) => {
    return state.contentPlanner.contentPlannerFilters.eventReligionFilters;
  });
  const filtersSource = useAppSelector((state) => {
    return state.contentPlanner.religions;
  });

  const dispatch = useAppDispatch();
  const onChange = (newFiltersState: FiltersHashmap): void => {
    dispatch(updateEventReligionFilters(newFiltersState));
  };

  return {
    filters,
    filtersStateSetter: onChange,
    filtersSource,
  };
};

export const useEventAreaFilters = (filterOfEventType: EventType): EventAreaFiltersFromStore => {
  const isRegional = filterOfEventType === EventType.REGIONAL;
  const isReligious = filterOfEventType === EventType.RELIGIOUS;

  if (!isRegional && !isReligious) {
    throw new Error(`invalid type ${filterOfEventType} provided`);
  }

  if (isRegional) {
    return useEventRegionFilters();
  }

  if (isReligious) {
    return useEventReligionFilters();
  }

  return {
    filters: {},
    filtersSource: [],
    filtersStateSetter: noop,
  };
};

export const useCustomEventFormData = (): BaseEvent => {
  return useAppSelector((state) => {
    return state.contentPlanner.customEventFormData;
  });
};

export const useIsNewEventBeingCreatedForDate = (date: Date): boolean => {
  return useAppSelector((state) => {
    return state.contentPlanner.isNewEventBeingCreated && areDatesEqual(date, getDateFromUnixTimestamp(state.contentPlanner.customEventFormData.timestamp));
  });
};

export const useCustomEventFormSetters = (): CustomEventFormSetters => {
  const dispatch = useAppDispatch();
  const onTitleChange = (newTitle: string): void => {
    dispatch(setCustomEventFormTitle(newTitle));
  };

  const onDescriptionChange = (newDescription: string): void => {
    dispatch(setCustomEventFormDescription(newDescription));
  };

  const onDateChange = (newDate: Date): void => {
    dispatch(setCustomEventFormTimestamp(getUnixTimestamp(newDate)));
  };

  return {onTitleChange, onDescriptionChange, onDateChange};
};

export const useAreEntriesPopulatedForCurrentPlannerDate = (): boolean => {
  const entriesMapKey = useEntriesMapKey();
  return useAppSelector((state) => {
    return state.contentPlanner.entriesMap[entriesMapKey]?.areEntriesPopulated ?? false;
  });
};

export const useContentPlannerEntriesMap = (): EntriesMap => {
  return useAppSelector((state) => {
    return state.contentPlanner.entriesMap;
  });
};

export const useContentPlannerEntriesForDate = (date: Date): ContentPlannerEntries => {
  return useAppSelector(({contentPlanner}) => {
    return getEntriesForDateFromStore(contentPlanner, date);
  }, areEntriesTheSame);
};

export const useCalendarEntriesViewType = (): CalendarEntriesViewType => {
  return useAppSelector(({contentPlanner}): CalendarEntriesViewType => {
    return contentPlanner.calendarEntriesViewType;
  });
};

export const useFilteredContentPlannerEntriesForDate = (date: Date): FilteredContentPlannerEntries => {
  const entriesViewType = useCalendarEntriesViewType();
  const entriesForDate = useContentPlannerEntriesForDate(date);
  const filters = useContentPlannerFilters();
  const filteredScheduledEntries = getFilteredScheduledEntriesByViewType(entriesViewType, entriesForDate.scheduledEntries, filters);
  const userEventFilters = useEventsFilters();

  return {
    contentOwnershipFilter: useContentOwnershipFilter(),
    internalEventEntries: getFilteredInternalEventsByViewType(entriesViewType, entriesForDate.internalEventEntries, filters),
    scheduledEntries: filteredScheduledEntries,
    userEventEntries: getUserEventEntriesByViewType(entriesViewType, entriesForDate.userEventEntries, filters, userEventFilters),
    socialPostEntries: getSocialPostEntriesByViewType(entriesViewType, entriesForDate.socialPostEntries, filters),
    emailCampaignEntries: getEmailCampaignEntriesByViewType(entriesViewType, entriesForDate.emailCampaignEntries, filters),
  };
};

export const useLoadPlannerEntries = (calendarEntriesViewType: CalendarEntriesViewType): void => {
  const dispatch = useAppDispatch();
  const contentPlannerDate = useContentPlannerDate();
  const entriesMap = useContentPlannerEntriesMap();
  const entriesMapKey = useEntriesMapKey();
  const contentOwnershipFilter = useContentOwnershipFilter();
  const entriesViewTypeFromStore = useCalendarEntriesViewType();
  const calendarEntriesRef = useRef<CalendarEntriesViewType>(calendarEntriesViewType);
  calendarEntriesRef.current = calendarEntriesViewType;

  const getContentPlannerEntries = (args: ContentPlannerThunkParams): void => {
    dispatch(getEntriesForContentPlanner(args))
      .then((reducer) => {
        const entryData = reducer.payload as ContentPlannerEntries;
        if (entryData?.lazyLoadingData === undefined || calendarEntriesRef.current !== args.calendarEntriesViewType) {
          return;
        }

        const {lazyLoadingData} = entryData;
        const {entryTypeLazyLoadData} = lazyLoadingData;

        if (!areMoreLazyLoadingEntriesNeeded(entryTypeLazyLoadData)) {
          return;
        }

        const lazyLoadArgs: ContentPlannerThunkParams = {
          fromTimestamp: parseInt(lazyLoadingData.dateFrom, 10),
          toTimestamp: parseInt(lazyLoadingData.dateTo, 10),
          contentOwnership: contentOwnershipFilter,
          lazyLoadingInfo: entryTypeLazyLoadData,
          calendarEntriesViewType,
        };

        getContentPlannerEntries(lazyLoadArgs);
      })
      .catch((): void => {
        window.PMW.openErrorModal({
          message: window.i18next.t('pmwjs_error_message'),
        });
      });
  };

  const getEntriesFromServer = (): void => {
    const args: ContentPlannerThunkParams = {
      fromTimestamp: getUnixTimestamp(getMonthlyCalendarStartDate(contentPlannerDate)),
      toTimestamp: getUnixTimestamp(getMonthlyCalendarEndDate(DEFAULT_NUM_ROWS, contentPlannerDate)),
      calendarEntriesViewType,
      contentOwnership: contentOwnershipFilter,
    };
    getContentPlannerEntries(args);
  };

  useEffect(() => {
    if (entriesMap[entriesMapKey]?.areEntriesPopulated) {
      return;
    }

    if (entriesViewTypeFromStore !== calendarEntriesViewType) {
      return;
    }

    getEntriesFromServer();
  }, [entriesMapKey, calendarEntriesViewType, entriesViewTypeFromStore]);
};
export const useInitContentPlanner = (): void => {
  useEffect(() => {
    showEventsIntroPopupIfNeeded();
  }, []);
};

export const useOpenEventTemplatesDialog = (): void => {
  const initTemplateDialog = (): void => {
    const eventHashedId = getEventHashedIdFromUrlHash();

    window.PMW.util.require(typeof window.PMW.contentPlannerEvents === 'undefined', 'eventTemplatesDialog', true, () => {
      if (eventHashedId) {
        window.PMW.contentPlannerEvents?.openEventTemplatesDialog(eventHashedId);
      } else {
        window.PMW.contentPlannerEvents?.closeEventTemplatesDialog();
      }
    });
  };

  useEffect(() => {
    initTemplateDialog();
  }, [window.location.hash]);
};

export const useAreFilteredEntriesPopulatedForDate = (date: Date): boolean => {
  const {internalEventEntries, userEventEntries, socialPostEntries, emailCampaignEntries, scheduledEntries} = useFilteredContentPlannerEntriesForDate(date);
  return internalEventEntries.length > 0 || scheduledEntries.length > 0 || userEventEntries.length > 0 || emailCampaignEntries.length > 0 || socialPostEntries.length > 0;
};

const getEntriesForDateFromStore = (contentPlannerState: ContentPlannerState, date: Date): ContentPlannerEntries => {
  const entriesMapKey = getEntriesMapKey(contentPlannerState.date);
  const formattedDate = getDayMonthYearFormat(date);

  if (contentPlannerState.entriesMap[entriesMapKey]?.entriesMappedByDate[formattedDate] === undefined) {
    return getEmptyEntriesObject();
  }

  return contentPlannerState.entriesMap[entriesMapKey].entriesMappedByDate[formattedDate];
};

const areEntriesTheSame = (val1: ContentPlannerEntries, val2: ContentPlannerEntries): boolean => {
  return (
    arraysEqual(val1.scheduledEntries, val2.scheduledEntries) &&
    arraysEqual(val1.internalEventEntries, val2.internalEventEntries) &&
    arraysEqual(val1.userEventEntries, val2.userEventEntries) &&
    arraysEqual(val1.emailCampaignEntries, val2.emailCampaignEntries) &&
    arraysEqual(val1.socialPostEntries, val2.socialPostEntries)
  );
};

const areContentPlannerDatesTheSame = (date1: Date, date2: Date): boolean => {
  return areDatesEqual(date1, date2);
};

const areFiltersTheSame = (filters1: ContentPlannerFilters, filters2: ContentPlannerFilters): boolean => {
  const areSocialGlobalFiltersSame = arraysEqual(Object.values(filters1.socialGlobalEventFilters), Object.values(filters2.socialGlobalEventFilters));
  const areRegionalFiltersSame = arraysEqual(Object.values(filters1.eventRegionFilters), Object.values(filters2.eventRegionFilters));
  const areReligiousFiltersSame = arraysEqual(Object.values(filters1.eventReligionFilters), Object.values(filters2.eventReligionFilters));
  const areEntryTypeFiltersSame = arraysEqual(Object.values(filters1.entryTypeFilters), Object.values(filters2.entryTypeFilters));
  const areOwnershipFiltersSame = arraysEqual(Object.values(filters1.contentOwnerFilters), Object.values(filters2.contentOwnerFilters));

  return areEntryTypeFiltersSame && areSocialGlobalFiltersSame && areRegionalFiltersSame && areReligiousFiltersSame && areOwnershipFiltersSame;
};
const getEventHashedIdFromUrlHash = (): string | null => {
  const hashSegments = window.location.hash.split('/');

  if (hashSegments.length < 3) {
    return null;
  }

  return hashSegments[2];
};
